import { FC, KeyboardEvent, MouseEvent, useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useMediaQuery } from 'react-responsive';
import theme from 'src/utils/theme';
import { Button } from 'src/components';
import { ButtonColor } from './Button';
import * as S from './styles';

const msgs = defineMessages({
  iDontKnow: { id: 'learning_session_next_button_i_dont_know' },
  checkAnswer: { id: 'learning_session_next_button_check_answer' },
  next: { id: 'Next' },
  continue: { id: 'learn_presentation_card_continue' },
  correct: { id: 'learning_session_next_button_correct_answer' },
});

export type NextActionState =
  | 'Unanswered'
  | 'Selected'
  | 'Processing'
  | 'CheckAnswer'
  | 'Correct'
  | 'NearlyCorrect'
  | 'Incorrect'
  | 'Viewed';

export type NextActionButtonProps = {
  state: NextActionState;
  onClick: (event?: MouseEvent) => void;
  onKeyUp: (event: KeyboardEvent) => void;
  isFirstPresentationCard?: boolean;
};

/** Disabling the button changes the styling to grey causing a flash of
 *  styling, so instead cause the button to temporarily do nothing while
 *  processing
 **/
const disabledClick = () => {};

// Time the button will remain disabled before it's clickable
export const nextBtnDisabledDelay = 500; // ms

export const NextActionButton: FC<NextActionButtonProps> = ({
  state,
  onClick,
  onKeyUp,
  isFirstPresentationCard,
  ...props
}) => {
  const [isDisabled, setIsDisabled] = useState(false);
  // Prevents clicking I don't know by mistake
  useEffect(() => {
    if (state === 'Unanswered' || state === 'Viewed') {
      setIsDisabled(true);
      const timeoutId = setTimeout(() => {
        setIsDisabled(false);
      }, nextBtnDisabledDelay);
      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [state]);

  const intl = useIntl();
  const isTabletOrLarger = useMediaQuery({
    query: `(min-device-width: ${theme.sz.md})`,
  });
  if (isFirstPresentationCard) {
    return (
      <Button
        {...props}
        wide
        buttonType="primary"
        buttonSize="medium"
        onClick={isDisabled ? disabledClick : onClick}
        onKeyUp={isDisabled ? disabledClick : onKeyUp}
      >
        {intl.formatMessage(msgs.continue)}
      </Button>
    );
  }

  if (state === 'Unanswered') {
    return (
      <S.ButtonThatFlipsForRTL
        vertical={isTabletOrLarger}
        buttonColor={ButtonColor.GREY}
        icon="question"
        onClick={isDisabled ? disabledClick : onClick}
        onKeyUp={isDisabled ? disabledClick : onKeyUp}
        {...props}
      >
        <S.ButtonText>{intl.formatMessage(msgs.iDontKnow)}</S.ButtonText>
      </S.ButtonThatFlipsForRTL>
    );
  }

  if (state === 'CheckAnswer' || state === 'Selected') {
    return (
      <S.ButtonThatFlipsForRTL
        vertical={isTabletOrLarger}
        buttonColor={ButtonColor.YELLOW}
        icon="chevronRight"
        onClick={onClick}
        onKeyUp={onKeyUp}
        {...props}
      >
        <S.ButtonText>{intl.formatMessage(msgs.checkAnswer)}</S.ButtonText>
      </S.ButtonThatFlipsForRTL>
    );
  }

  if (state === 'Correct') {
    return (
      <S.Button
        vertical={isTabletOrLarger}
        buttonColor={ButtonColor.GREEN}
        icon="check"
        onClick={onClick}
        onKeyUp={onKeyUp}
        {...props}
      >
        {intl.formatMessage(msgs.correct)}
      </S.Button>
    );
  }

  if (state === 'Incorrect' || state === 'NearlyCorrect' || state === 'Viewed') {
    return (
      <S.ButtonThatFlipsForRTL
        vertical={isTabletOrLarger}
        buttonColor={ButtonColor.YELLOW}
        icon="chevronRight"
        onClick={isDisabled ? disabledClick : onClick}
        onKeyUp={isDisabled ? disabledClick : onKeyUp}
        data-testid="next-button"
        {...props}
      >
        <S.ButtonText>{intl.formatMessage(msgs.next)}</S.ButtonText>
      </S.ButtonThatFlipsForRTL>
    );
  }

  /* istanbul ignore else: Internal inconsistency check */
  if (state === 'Processing') {
    return (
      <S.ButtonThatFlipsForRTL
        vertical={isTabletOrLarger}
        buttonColor={ButtonColor.YELLOW}
        icon="chevronRight"
        onClick={disabledClick} // Same as disabling button but doesn't override styles
        onKeyUp={disabledClick}
        {...props}
      >
        <S.ButtonText>{intl.formatMessage(msgs.next)}</S.ButtonText>
      </S.ButtonThatFlipsForRTL>
    );
  }

  /* istanbul ignore next: Internal inconsistency check */
  throw Error(`Unknown NextActionButton state ${state}`);
};
